import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Observable, Subject, fromEvent, map, distinctUntilChanged, debounceTime, filter } from 'rxjs';
import { MsgModel } from '../../msg/model/msg.model';
import { Country } from '../model/country.model';
import { PostalAddressService } from '../service/postal-address.service';

@Component({
    selector: 'input-country-autocomplete',
    templateUrl: './input-country-autocomplete.component.html',
    styleUrls: ['./input-country-autocomplete.component.scss']
})
export class InputCountryAutocompleteComponent implements OnInit {
    @Input() id: string = 'Country';
    @Input() name: string = 'Country';
    @Input() label: string = 'Country';
    @Input() placeholder: string = 'Country';
    @Input() required: boolean = false;
    @Input() disabledCountry: boolean = false;
    @Input() selectedCountry: Country = { COUNTRY_NAME: '' };
    @Output() selectedCountryChange: EventEmitter<Country> = new EventEmitter<Country>();
    @Input() disabled: boolean;
    @Input() displayRequiredMessage: boolean = false;


    @ViewChild('searchString') searchString!: NgModel;
    @ViewChild('searchStringInput', { static: true }) searchStringInput!: ElementRef;
    searchStringChanged$: Observable<any> = new Subject<any>();

    isLoading: boolean = false;
    msg!: MsgModel;
    requiredMsg!: MsgModel;

    minCharacters = 3;

    countries!: Country[];

    constructor(private postalAddressService: PostalAddressService) {}

    ngOnInit(): void {
        this.requiredMsg = new MsgModel();
        this.requiredMsg.MSG = this.name +  ' is required';
        // Search by ID if given an ID
        if (this.selectedCountry.ID! > 0) {
            this.isLoading = true;
            this.searchCountryById(this.selectedCountry.ID).subscribe((res: any) => {
                this.isLoading = false;
                if (res['country'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    this.onSelect(res['country']);
                }
            });
        }
        // Search by ID if COUNTRY_NAME is a number
        else if (this.selectedCountry.COUNTRY_NAME && !isNaN(Number(this.selectedCountry.COUNTRY_NAME))) {
            this.isLoading = true;
            this.searchCountryById(Number(this.selectedCountry.COUNTRY_NAME)).subscribe((res: any) => {
                this.isLoading = false;
                if (res['country'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    this.onSelect(res['country']);
                }
            });
        }
        // Search by COUNTRY_NAME w/ no ID
        else if (this.selectedCountry.COUNTRY_NAME && this.selectedCountry.COUNTRY_NAME.length > this.minCharacters) {
            this.isLoading = true;
            this.searchCountry(this.selectedCountry.COUNTRY_NAME).subscribe((res: any) => {
                this.isLoading = false;
                if (res['countries'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    if (res.countries.length === 1) {
                        this.onSelect(res['countries']);
                    } else {
                        for (var i = 0; i < res.states?.length; i++) {
                            if (res.states[i].CITY_NAME === this.selectedCountry.COUNTRY_NAME) {
                                this.onSelect(res.countries[i]);
                                break;
                            }
                        }
                    }
                }
            });
        }

        fromEvent(this.searchStringInput.nativeElement, 'input')
            .pipe(
                // get value
                map((event: any) => {
                    return event.target.value;
                }),
                // only search when value has changed
                distinctUntilChanged(),
                // 500 ms delay before searching
                debounceTime(500),
                // only search if search string is >= 3 characters
                filter((res) => {
                    let canSearch = res !== null && res.length >= this.minCharacters;

                    let newCountry = new Country();
                    newCountry.COUNTRY_NAME = res;
                    this.onSelect(newCountry);

                    return canSearch;
                })
            )
            .subscribe((text: string) => {
                this.isLoading = true;

                this.searchCountry(text).subscribe((res: any) => {
                    this.isLoading = false;
                    if (res['countries'] == undefined) {
                        let newCountry = new Country();
                        newCountry.COUNTRY_NAME = text;
                        this.onSelect(newCountry);
                        this.countries = [];
                    } else {
                        this.countries = res['countries'];
                    }
                });
            });
    }

    autoSelect(countries?: Country[]) {
        if (!countries) {
            return;
        } else if (countries.length === 0) {
            return;
        } else if (countries.length === 1) {
            for (var i = 0; i < countries.length; i++) {
                if (countries[i].COUNTRY_NAME === this.selectedCountry.COUNTRY_NAME) {
                    this.onSelect(countries[i]);
                    break;
                }
            }
        }
    }

    searchCountry(searchString: any): Observable<Country[]> {
        return this.postalAddressService.searchCountries(searchString);
    }

    searchCountryById(id: any): Observable<Country[]> {
        return this.postalAddressService.searchCountriesById(id);
    }

    onSelect(country: Country) {
        this.selectedCountry = country;
        this.selectedCountryChange.emit(this.selectedCountry);

        this.searchString.control.setValue(this.selectedCountry.COUNTRY_NAME);
        this.checkForId(this.searchString);

        this.clearAutocompleteArray();
    }

    clearAutocompleteArray() {
        this.countries = [];
    }

    checkForId(input: NgModel) {
        if (this.selectedCountry.COUNTRY_NAME && this.selectedCountry.COUNTRY_NAME.length === 0) {
            input.control.setErrors({ Required: 'New Country will be created'});
        }
        if (!this.selectedCountry.ID) {
            input.control.setErrors({ idRequired: 'New Country will be created'});
        } else {
            input.control.setErrors(null);
        }
    }
}
